<script setup>
/* eslint-disable vue/no-root-v-if */
import { computed } from 'vue';
import { useArticleComponentMap } from '@/Composables/articleComponentMap.js';
import { useIsPastDateTime } from '@/Composables/localizedDateTime.js';
import ListItem from '@/Components/Cards/ListItem.vue';

const props = defineProps({
    relatedContent: {
        type: Object,
        default: () => ({ articles: [], events: [] }),
    },
    siteName: {
        type: String,
        default: 'german',
    },
    locale: {
        type: String,
        default: 'de',
    },
});

const hasRelatedContent = computed(() => props.relatedContent && Object.values(props.relatedContent).some(array => array.length > 0));
</script>

<template>
    <div
        v-if="hasRelatedContent"
        class="mt-16 lg:mt-24 space-y-8"
    >
        <h2 class="font-semibold text-xl md:text-2xl text-deep-teal-700">
            {{ $t('eventDetails.headline.relatedContent') }}
        </h2>

        <div class="grid grid-cols-1 gap-8">
            <template
                v-for="(collection, key) in relatedContent"
                :key
            >
                <template v-if="key === 'articles'">
                    <div
                        v-for="article in collection"
                        :key="article.url"
                    >
                        <component
                            :is="useArticleComponentMap(article.collectionType)"
                            :title="article.title"
                            :teaser="article.teaser"
                            :url="article.url"
                            :business-areas="article.event_business_areas"
                            :topics="article.event_topics"
                            :advertorial="article.advertorial"
                            :type="article.collectionType === 'publications' ? article.category : article.collectionType"
                            :date="article.published_at"
                            :contributors="article.contributor_profiles?.map(profile => ({ name: profile.fullname }))"
                            :image="{ ...article.images?.card, alt: article.images?.alt }"
                            :is-external="article.site !== siteName || article.collectionType === 'publications'"
                            :is-publication="article.collectionType === 'publications'"
                        />
                    </div>
                </template>

                <template v-else-if="key === 'events'">
                    <div
                        v-for="programPackage in collection"
                        :key="programPackage.url"
                    >
                        <ListItem
                            :title="programPackage.title"
                            :teaser="programPackage.teaser"
                            :image="programPackage.glide_teaser_image_url_avif ? {
                                avif: programPackage.glide_teaser_image_url_avif,
                                webp: programPackage.glide_teaser_image_url_webp,
                                jpg: programPackage.glide_teaser_image_url_jpg,
                                alt: programPackage.teaser_image_alt_with_fallback,
                            } : null"
                            :url="programPackage.external_url || programPackage.url"
                            :type="programPackage.event_format?.event_format_category?.name"
                            :business-areas="programPackage.event_business_areas"
                            :topics="programPackage.event_topics"
                            :attendee-count="programPackage.attendee_count"
                            :start-date="programPackage.start_date"
                            :end-date="programPackage.end_date"
                            :location="programPackage.location?.display_name || (programPackage.event_format?.event_type !== 'external' ? 'Online' : null)"
                            :repetition-dates-count="programPackage.event_container.mode === 'repetition' ? (programPackage.event_container.program_packages_count - 1) : null"
                            :is-inactive="useIsPastDateTime(programPackage.start_date)"
                            :is-external="programPackage.event_format?.event_type === 'external' || programPackage?.event_format.language !== locale"
                        />
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>
